import { Capacitor } from '@capacitor/core';

import { getAppUrlScheme, getHubUrl, getKeycloakCallbackPath } from './keycloakConfig';

/**
 * Builds the OAuth redirect URI.
 * - Web: `origin + callbackPath`
 * - Native with `hubUrl`: HTTPS App Link (`https://app…/login`) for Capgo WebView interception
 * - Native without `hubUrl`: custom scheme fallback (`in.rabatteries.gold://login`)
 */
export function getKeycloakRedirectUri(): string {
	const callbackPath = getKeycloakCallbackPath();

	if (Capacitor.isNativePlatform()) {
		const hubUrl = getHubUrl();

		if (hubUrl) {
			return `${hubUrl}${callbackPath}`;
		}

		const host = callbackPath.replace(/^\//, '');

		return `${getAppUrlScheme()}://${host}`;
	}

	return `${window.location.origin}${callbackPath}`;
}

/**
 * Returns whether `url` is a custom-scheme OAuth callback (login host only).
 * Other scheme hosts (e.g. `in.rabatteries.gold://share`) must not be treated as OAuth —
 * that incorrectly clears the session and sends the user to the login page.
 */
export function isCustomSchemeOAuthCallbackUrl(url: string): boolean {
	const customPrefix = `${getAppUrlScheme()}://`;

	if (!url.startsWith(customPrefix)) {
		return false;
	}

	const remainder = url.slice(customPrefix.length);
	const suffixStart = remainder.search(/[?#]/);
	const hostPart = suffixStart === -1 ? remainder : remainder.slice(0, suffixStart);
	const callbackHost = getKeycloakCallbackPath().replace(/^\//, '');
	const normalizedHost = hostPart.replace(/^\//, '');

	return normalizedHost === callbackHost || normalizedHost.startsWith(`${callbackHost}/`);
}

/**
 * Returns whether `url` is a native OAuth callback (custom scheme or HTTPS hub App Link).
 */
export function isNativeOAuthCallbackUrl(url: string): boolean {
	if (isCustomSchemeOAuthCallbackUrl(url)) {
		return true;
	}

	const hubUrl = getHubUrl();

	if (!hubUrl) {
		return false;
	}

	try {
		const parsed = new URL(url);
		const hub = new URL(hubUrl);
		const callbackPath = getKeycloakCallbackPath().replace(/\/$/, '') || '/';
		const pathOnly = parsed.pathname.replace(/\/$/, '') || '/';

		return (
			parsed.protocol === 'https:' &&
			parsed.host === hub.host &&
			pathOnly === callbackPath
		);
	} catch {
		return false;
	}
}

/** Returns whether `url` is a post-logout deep link to the login callback (no OAuth params). */
export function isNativeLogoutReturnUrl(url: string): boolean {
	if (!isNativeOAuthCallbackUrl(url)) {
		return false;
	}

	const historyPath = oauthDeepLinkToHistoryPath(url);

	if (hasOAuthCallbackParams(historyPath)) {
		return false;
	}

	const callbackPath = getKeycloakCallbackPath();
	const pathOnly = historyPath.split(/[?#]/)[0]?.replace(/\/$/, '') || '/';
	const normalizedCallback = callbackPath.replace(/\/$/, '') || '/';

	return pathOnly === normalizedCallback;
}

/** Converts a native deep-link or HTTPS App Link URL into a browser history path. */
export function oauthDeepLinkToHistoryPath(deepLinkUrl: string): string {
	const callbackPath = getKeycloakCallbackPath();
	const customPrefix = `${getAppUrlScheme()}://`;

	if (deepLinkUrl.startsWith(customPrefix)) {
		const remainder = deepLinkUrl.slice(customPrefix.length);
		const suffixStart = remainder.search(/[?#]/);
		const hostPart = suffixStart === -1 ? remainder : remainder.slice(0, suffixStart);
		const suffix = suffixStart === -1 ? '' : remainder.slice(suffixStart);
		const path = hostPart.startsWith('/')
			? hostPart
			: `/${hostPart || callbackPath.replace(/^\//, '')}`;

		return `${path}${suffix}`;
	}

	try {
		const parsed = new URL(deepLinkUrl);

		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch {
		return callbackPath;
	}
}

/** Returns whether a path or hash contains standard OAuth callback parameters. */
export function hasOAuthCallbackParams(pathWithQuery: string): boolean {
	const queryIndex = pathWithQuery.indexOf('?');
	const hashIndex = pathWithQuery.indexOf('#');
	const paramString =
		queryIndex !== -1
			? pathWithQuery.slice(queryIndex + 1, hashIndex !== -1 && hashIndex > queryIndex ? hashIndex : undefined)
			: hashIndex !== -1
				? pathWithQuery.slice(hashIndex + 1)
				: '';
	const params = new URLSearchParams(paramString);

	return params.has('code') || params.has('error') || params.has('session_state');
}

/** Returns the current browser location as a path with query and hash. */
export function getCurrentOAuthCallbackPath(): string {
	if (typeof window === 'undefined') {
		return getKeycloakCallbackPath();
	}

	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/** Returns whether the current URL contains OAuth callback parameters. */
export function hasOAuthCallbackInCurrentUrl(): boolean {
	return hasOAuthCallbackParams(getCurrentOAuthCallbackPath());
}

/**
 * When an OAuth callback lands on the wrong path (e.g. `/login/redirect?code=…`),
 * rewrites the URL to the configured callback path so `keycloak.init()` can exchange the code.
 */
export function redirectOAuthCallbackToLoginPath(): boolean {
	if (typeof window === 'undefined' || !hasOAuthCallbackInCurrentUrl()) {
		return false;
	}

	const callbackPath = getKeycloakCallbackPath();
	const pathname = window.location.pathname.replace(/\/$/, '') || '/';

	if (pathname === callbackPath) {
		return false;
	}

	window.location.replace(
		`${window.location.origin}${callbackPath}${window.location.search}${window.location.hash}`,
	);

	return true;
}

/** Removes OAuth callback query/hash parameters from the current URL without reloading. */
export function clearOAuthCallbackFromUrl(): void {
	if (typeof window === 'undefined' || !hasOAuthCallbackInCurrentUrl()) {
		return;
	}

	const { pathname, search, hash } = window.location;
	const queryIndex = search.indexOf('?');
	const hashIndex = hash.indexOf('#');

	const stripOAuthParams = (paramString: string): string => {
		const params = new URLSearchParams(paramString);
		params.delete('code');
		params.delete('state');
		params.delete('session_state');
		params.delete('error');
		params.delete('error_description');
		params.delete('iss');

		return params.toString();
	};

	const cleanedSearch = queryIndex !== -1 ? stripOAuthParams(search.slice(queryIndex + 1)) : '';
	const cleanedHash = hashIndex !== -1 ? stripOAuthParams(hash.slice(hashIndex + 1)) : '';

	const nextUrl =
		pathname +
		(cleanedSearch ? `?${cleanedSearch}` : '') +
		(cleanedHash ? `#${cleanedHash}` : '');

	window.history.replaceState({}, '', nextUrl);
}
